import React from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { Plus } from 'lucide-react'
import { AppLayout } from '../../components/layout/AppLayout'
import { Button } from '../../components/ui/Button'
import { apiFetch } from '../../lib/api-client'
import { t } from '../../lib/i18n'

interface Property {
  id: string
  address: string
}

interface Lease {
  id: string
  property_id: string
  unit_no: string
  type: string
  usage_type: string
  area_m2: number | null
  room_count: number | null
}

export function LeaseListPage() {
  const strings = t()
  const navigate = useNavigate()
  const [leases, setLeases] = React.useState<Lease[]>([])
  const [propertiesById, setPropertiesById] = React.useState<Record<string, Property>>({})
  const [loading, setLoading] = React.useState(true)
  const [error, setError] = React.useState<string | null>(null)
  const generalError = strings.general.error

  React.useEffect(() => {
    Promise.all([
      apiFetch<Lease[]>('/api/v1/leases'),
      apiFetch<{ items: Property[] }>('/api/v1/properties'),
    ])
      .then(([leasesData, propertiesData]) => {
        setLeases(leasesData)
        const byId: Record<string, Property> = {}
        for (const p of propertiesData.items) byId[p.id] = p
        setPropertiesById(byId)
      })
      .catch(() => setError(generalError))
      .finally(() => setLoading(false))
  }, [generalError])

  const usageLabel = (usage: string) => {
    const map: Record<string, string> = {
      bolig: strings.leases.usageBolig,
      erhverv: strings.leases.usageErhverv,
      kontor: strings.leases.usageKontor,
      lager: strings.leases.usageLager,
      garage: strings.leases.usageGarage,
      kælder: strings.leases.usageKaelder,
      andet: strings.leases.usageAndet,
    }
    return map[usage] ?? usage
  }

  return (
    <AppLayout
      actions={
        <Link to="/lejemaal/opret">
          <Button size="sm">
            <Plus size={13} style={{ marginRight: 4 }} />
            {strings.leases.create}
          </Button>
        </Link>
      }
    >
      <div style={{ background: '#fff', border: '1px solid #e8eaed', borderRadius: 10, boxShadow: '0 1px 3px rgba(0,0,0,0.06)', overflow: 'hidden' }}>
        <div style={{ padding: '16px 24px 12px', borderBottom: '1px solid #e8eaed', fontWeight: 600, fontSize: 14, color: '#111827' }}>
          {strings.leases.title} {!loading && <span style={{ fontWeight: 400, color: '#9ca3af', fontSize: 13 }}>({leases.length})</span>}
        </div>

        {loading && <div style={{ padding: 48, textAlign: 'center', color: '#9ca3af', fontSize: 13 }}>Henter…</div>}
        {error && <div style={{ padding: 20, color: '#dc2626', fontSize: 13 }}>{error}</div>}

        {!loading && !error && leases.length === 0 && (
          <div style={{ padding: 56, textAlign: 'center' }}>
            <p style={{ color: '#9ca3af', fontSize: 13, marginBottom: 16 }}>{strings.leases.noResults}</p>
            <Link to="/lejemaal/opret"><Button size="sm"><Plus size={13} style={{ marginRight: 4 }} />{strings.leases.create}</Button></Link>
          </div>
        )}

        {!loading && !error && leases.length > 0 && (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ background: '#f9fafb' }}>
                {[strings.leases.address, strings.leases.unit, strings.leases.usage, 'Areal', ''].map((h) => (
                  <th key={h} style={{ padding: '10px 24px', textAlign: 'left', fontSize: 11, fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em', borderBottom: '1px solid #e8eaed', whiteSpace: 'nowrap' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {leases.map((l, i) => (
                <LeaseRow key={l.id} lease={l} property={propertiesById[l.property_id]} typeLabel={usageLabel(l.usage_type)} isLast={i === leases.length - 1} onClick={() => navigate(`/lejemaal/${l.id}`)} />
              ))}
            </tbody>
          </table>
        )}
      </div>
    </AppLayout>
  )
}

function LeaseRow({ lease: l, property, typeLabel, isLast, onClick }: {
  lease: Lease
  property?: Property
  typeLabel: string
  isLast: boolean
  onClick: () => void
}) {
  const [hovered, setHovered] = React.useState(false)

  return (
    <tr
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{ background: hovered ? '#f9fafb' : 'transparent', transition: 'background 0.1s', borderBottom: isLast ? 'none' : '1px solid #f3f4f6', cursor: 'pointer' }}
    >
      <td style={{ padding: '12px 24px', color: '#6b7280' }}>{property?.address ?? <span style={{ color: '#d1d5db' }}>—</span>}</td>
      <td style={{ padding: '12px 24px', fontWeight: 500, color: '#111827' }}>{l.unit_no}</td>
      <td style={{ padding: '12px 24px', color: '#6b7280' }}>{typeLabel}</td>
      <td style={{ padding: '12px 24px', color: '#6b7280' }}>
        {l.area_m2 != null ? `${l.area_m2} m²` : <span style={{ color: '#d1d5db' }}>—</span>}
      </td>
      <td style={{ padding: '12px 24px', textAlign: 'right', color: '#9ca3af', fontSize: 13 }}>→</td>
    </tr>
  )
}
